import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import {
  Collapse,
  ListItem,
  IconButton,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { VariableSizeList as List } from 'react-window';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { ValueLabel, ValueLabelGroup } from 'types/types';

import ListSearchBar, { Ref as ListSearchBarRef } from './ListSearchBar';
import { Ref as AlarmManagementFormRulesRef } from '../FormRules/FormRules';

export interface ListRef {
  items: ValueLabelGroup[];
  handleChangeItems: (items: ValueLabelGroup[]) => void;
}

interface ListProps {
  initialItems: ValueLabelGroup[];
  handleCheckItem: (group: string, item: ValueLabel) => void;
  alarmManagementFormRulesRef: React.MutableRefObject<AlarmManagementFormRulesRef | null>;
}

const Row: React.FC<{
  index: number;
  toggleCollapse: (index: number) => void;
  collapsedItems: {
    [key: number]: boolean;
  };
  item: ValueLabelGroup;
  handleCheckItem: (group: string, item: ValueLabel) => void;
}> = ({ index, collapsedItems, toggleCollapse, item, handleCheckItem }) => {
  const isCollapsed = collapsedItems[index];

  return (
    <div>
      <ListItemButton
        onClick={() => toggleCollapse(index)}
        className="collapse-title"
      >
        <ListItemText primary={item.group} />
        {!isCollapsed ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
        <div style={{ paddingLeft: '20px' }}>
          <InnerList
            items={item.items}
            handleCheckItem={handleCheckItem}
            group={item.group}
          />
        </div>
      </Collapse>
    </div>
  );
};

const InnerList = ({
  group,
  items,
  handleCheckItem,
}: {
  group: string;
  items: ValueLabel[];
  handleCheckItem: (group: string, item: ValueLabel) => void;
}) => {
  const handleClickItem = (index: number) => {
    if (!items[index].enableDeleteButton) {
      handleCheckItem(group, items[index]);
    }
  };
  return (
    <List
      height={items.length > 6 ? 210 : items.length * 35}
      itemCount={items.length}
      itemSize={() => 35}
      width="100%"
    >
      {({ index, style }) => (
        <ListItem
          key={items[index].value}
          style={style}
          onClick={() => handleClickItem(index)}
          divider
          className="list-item"
        >
          <ListItemText primary={items[index].label} />
          {items[index].enableDeleteButton && (
            <IconButton onClick={() => handleCheckItem(group, items[index])}>
              <ClearIcon />
            </IconButton>
          )}
        </ListItem>
      )}
    </List>
  );
};

const VirtualizedCollapsibeList: ForwardRefRenderFunction<
  ListRef,
  ListProps
> = ({ initialItems, handleCheckItem, alarmManagementFormRulesRef }, ref) => {
  const allItems = useRef<ValueLabelGroup[]>([...initialItems]);
  const [items, setItems] = useState<ValueLabelGroup[]>([...initialItems]);
  const [searchItems, setSearchItems] = useState<ValueLabelGroup[]>([]);
  const searchRef = useRef<ListSearchBarRef>(null);

  const [collapsedItems, setCollapsedItems] = useState<{
    [key: number]: boolean;
  }>({});

  const listRef = useRef<List>(null);

  const handleChangeItems = (
    itemsUpdated: ValueLabelGroup[],
    searchFunctionality: boolean = false
  ) => {
    if (
      alarmManagementFormRulesRef.current?.refs
        .aggregationLevelsForDimAttributes
    ) {
      if (!searchFunctionality) {
        allItems.current = [...itemsUpdated];
        setItems([...itemsUpdated]);
        if (searchItems.length > 0) {
          const searchItemsLeft =
            searchRef.current?.searchInValueLabelGroupsAll(
              itemsUpdated,
              searchRef.current.searchValue
            );
          if (searchItemsLeft && searchItemsLeft.length === 0) {
            searchRef.current?.setSearchValue('');
          }
          setSearchItems(searchItemsLeft ? [...searchItemsLeft] : []);
        }
      } else {
        setSearchItems([...itemsUpdated]);
      }
    }
  };

  const toggleCollapse = (index: number) => {
    setCollapsedItems((prev) => {
      const newCollapsedItems = { ...prev, [index]: !prev[index] };
      listRef.current?.resetAfterIndex(index);
      return newCollapsedItems;
    });
  };

  useImperativeHandle(ref, () => ({
    handleChangeItems,
    items,
  }));

  useEffect(() => {
    if (initialItems) {
      setItems(initialItems);
    }
    allItems.current = initialItems;
  }, [initialItems]);

  return (
    <div>
      <ListSearchBar
        items={items}
        handleChangeItems={handleChangeItems}
        allItems={allItems}
        ref={searchRef}
      />
      {searchItems.length > 0
        ? searchItems.map((el, index) => {
            return (
              <Row
                key={`${el.group}-${index}`}
                index={index}
                collapsedItems={collapsedItems}
                item={el}
                toggleCollapse={toggleCollapse}
                handleCheckItem={handleCheckItem}
              />
            );
          })
        : items.map((el, index) => {
            return (
              <Row
                key={`${el.group}-${index}`}
                index={index}
                collapsedItems={collapsedItems}
                item={el}
                toggleCollapse={toggleCollapse}
                handleCheckItem={handleCheckItem}
              />
            );
          })}
    </div>
  );
};

export default forwardRef<ListRef, ListProps>(VirtualizedCollapsibeList);
